import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaTimes,
  FaHdd,
  FaMicrochip,
  FaMemory,
  FaNetworkWired,
  FaWindows,
  FaBarcode,
  FaClock,
  FaShieldAlt,
  FaSpinner,
} from "react-icons/fa";

import { fetchAssetDetail } from "../../api/overviewApi";

const ACCENT = "#6366f1";

/** Colour + copy for an asset's check-in freshness. */
export const getFreshnessStyle = (days, staleDays = 30) => {
  if (days === null || days === undefined) {
    return { color: "#6b7280", background: "rgba(107,114,128,0.15)", label: "Never reported" };
  }
  if (days >= 90) return { color: "#ef4444", background: "rgba(239,68,68,0.15)", label: `${days}d ago` };
  if (days >= staleDays) return { color: "#f59e0b", background: "rgba(245,158,11,0.15)", label: `${days}d ago` };
  if (days >= 7) return { color: "#6366f1", background: "rgba(99,102,241,0.15)", label: `${days}d ago` };
  return { color: "#10b981", background: "rgba(16,185,129,0.15)", label: days <= 1 ? "Today" : `${days}d ago` };
};

/** Colour per normalised asset type so charts, table and modal stay consistent. */
export const getAssetTypeStyle = (assetType) => {
  const styles = {
    server: { color: "#8b5cf6", background: "rgba(139,92,246,0.15)" },
    virtual_server: { color: "#a855f7", background: "rgba(168,85,247,0.15)" },
    workstation: { color: "#3b82f6", background: "rgba(59,130,246,0.15)" },
    virtual_workstation: { color: "#06b6d4", background: "rgba(6,182,212,0.15)" },
    network: { color: "#10b981", background: "rgba(16,185,129,0.15)" },
    printer: { color: "#f59e0b", background: "rgba(245,158,11,0.15)" },
    other: { color: "#6b7280", background: "rgba(107,114,128,0.15)" },
  };
  return styles[assetType] || styles.other;
};

const formatDate = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SpecRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "9px 0", borderBottom: "1px solid var(--color-border)" }}>
    <span style={{ fontSize: 12, color: "var(--color-text-light)", fontWeight: 500, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, color: "var(--color-text)", fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>
      {value || "—"}
    </span>
  </div>
);

const SpecSection = ({ icon: Icon, title, children }) => (
  <div
    style={{
      background: "var(--color-bg-card)",
      border: "1px solid var(--color-border)",
      borderRadius: 14,
      padding: 16,
      height: "100%",
    }}
  >
    <div className="d-flex align-items-center gap-2 mb-2">
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: "rgba(99,102,241,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={13} color={ACCENT} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>{title}</span>
    </div>
    {children}
  </div>
);

/**
 * Full specification for one managed asset. `asset` is the row from the inventory table;
 * the modal re-fetches the record so it always shows current detail.
 */
const AssetDetailModal = ({ asset, staleDays = 30, onClose }) => {
  const [detail, setDetail] = useState(asset);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchAssetDetail(asset?.id)
      .then((result) => {
        if (cancelled) return;
        if (result) setDetail(result);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [asset?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const typeStyle = getAssetTypeStyle(detail?.asset_type);
  const freshness = getFreshnessStyle(detail?.days_since_contact, staleDays);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.18 }}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 820,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--color-bg)",
          borderRadius: 18,
          border: "1px solid var(--color-border)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid var(--color-border)",
            // Opaque sticky header so scrolled content cannot bleed through.
            background: "linear-gradient(135deg, color-mix(in srgb, #6366f1 14%, var(--color-bg-card)) 0%, color-mix(in srgb, #8b5cf6 10%, var(--color-bg-card)) 100%)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            position: "sticky",
            top: 0,
            zIndex: 5,
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "rgba(99,102,241,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FaHdd size={19} color={ACCENT} />
            </div>
            <div>
              <h5 className="fw-bold mb-1" style={{ color: "var(--color-text)", fontSize: 17, wordBreak: "break-all" }}>
                {detail?.hostname || "Unknown asset"}
              </h5>
              <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                <span style={{ ...typeStyle, padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                  {detail?.asset_type_label || "Other"}
                </span>
                <span style={{ background: freshness.background, color: freshness.color, padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                  Last seen {freshness.label}
                </span>
                {detail?.is_missing_in_cw && (
                  <span style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                    Not in last sync
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close asset detail"
            style={{ border: "none", background: "transparent", color: "var(--color-text-light)", fontSize: 18, lineHeight: 1, cursor: "pointer" }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {loading && (
            <div className="d-flex align-items-center gap-2 mb-3" style={{ color: "var(--color-text-light)", fontSize: 12 }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}>
                <FaSpinner size={12} />
              </motion.div>
              Refreshing asset details...
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <SpecSection icon={FaBarcode} title="Identity">
                <SpecRow label="Hostname" value={detail?.hostname} />
                <SpecRow label="Friendly name" value={detail?.friendly_name} />
                <SpecRow label="Serial number" value={detail?.serial_number} />
                <SpecRow label="Domain" value={detail?.domain} />
                <SpecRow label="ConnectWise type" value={detail?.endpoint_type} />
              </SpecSection>
            </div>

            <div className="col-md-6">
              <SpecSection icon={FaWindows} title="Operating system">
                <SpecRow label="OS" value={detail?.os_name} />
                <SpecRow label="Version" value={detail?.os_version} />
                <SpecRow label="Release" value={detail?.os_display_version} />
                <SpecRow label="Virtual" value={detail?.is_virtual ? detail?.virtual_platform || "Yes" : "No (physical)"} />
                <SpecRow label="Chassis" value={detail?.chassis} />
              </SpecSection>
            </div>

            <div className="col-md-6">
              <SpecSection icon={FaMicrochip} title="Hardware">
                <SpecRow label="Manufacturer" value={detail?.manufacturer} />
                <SpecRow label="Model" value={detail?.model} />
                <SpecRow label="CPU" value={detail?.cpu_model} />
                <SpecRow label="Cores" value={detail?.cpu_cores} />
                <SpecRow label="TPM" value={detail?.tpm_enabled === null || detail?.tpm_enabled === undefined ? "—" : detail.tpm_enabled ? "Enabled" : "Disabled"} />
              </SpecSection>
            </div>

            <div className="col-md-6">
              <SpecSection icon={FaMemory} title="Capacity">
                <SpecRow label="Memory" value={detail?.ram_gb ? `${detail.ram_gb} GB` : null} />
                <SpecRow label="Storage" value={detail?.storage_gb ? `${detail.storage_gb} GB` : null} />
              </SpecSection>
            </div>

            <div className="col-md-6">
              <SpecSection icon={FaNetworkWired} title="Network">
                <SpecRow label="IP address" value={detail?.ip_address} />
                <SpecRow label="MAC address" value={detail?.mac_address} />
                {Array.isArray(detail?.ip_addresses) && detail.ip_addresses.length > 1 && (
                  <SpecRow label="All IPs" value={detail.ip_addresses.join(", ")} />
                )}
              </SpecSection>
            </div>

            <div className="col-md-6">
              <SpecSection icon={FaClock} title="Timeline">
                <SpecRow label="First seen in ConnectWise" value={formatDate(detail?.first_seen)} />
                <SpecRow label="Last contact" value={formatDate(detail?.last_contact)} />
                <SpecRow label="Last synced to OneView" value={formatDate(detail?.synced_at)} />
              </SpecSection>
            </div>
          </div>

          <div
            className="d-flex align-items-center gap-2 mt-3"
            style={{
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 12,
              padding: "10px 14px",
            }}
          >
            <FaShieldAlt size={12} color={ACCENT} />
            <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
              Reported by the ConnectWise RMM agent. Missing values mean the agent could not read that
              property from the device.
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AssetDetailModal;
